import { useMemo } from 'react';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { HistoricalChart } from '../charts/HistoricalChart';
import {
  EarningsChart,
  RevenueChart,
  CashFlowChart,
  MarginChart,
  EPSGrowthChart,
  ShareDilutionChart,
  SharesOutstandingChart,
  FCFPerShareChart,
} from '../charts/FundamentalsChart';
import type { ChatChartData } from '../../store';

interface ChatChartProps {
  chart: ChatChartData;
  compact?: boolean;
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatPrice = (value: number) => `$${value.toFixed(2)}`;

interface PriceTooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
    dataKey: string;
  }>;
  label?: string;
}

const PriceTooltip = ({ active, payload, label }: PriceTooltipProps) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-[var(--color-bg-primary)] border border-[var(--color-border)] rounded-lg p-3 shadow-lg">
      <p className="text-sm font-medium text-[var(--color-text-primary)] mb-2">
        {label ? formatDate(label) : ''}
      </p>
      <div className="space-y-1">
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-[var(--color-text-muted)]">{entry.name}:</span>
            <span className="font-medium text-[var(--color-text-primary)]">
              {formatPrice(entry.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

function PriceChart({ data, title }: { data: Record<string, any>[]; title: string }) {
  const chartData = useMemo(() => {
    return data.filter(d => d.close != null);
  }, [data]);

  const priceDomain = useMemo(() => {
    const prices = chartData.map(d => d.close as number);
    if (prices.length === 0) return [0, 100];
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const padding = (max - min) * 0.1;
    return [Math.floor(min - padding), Math.ceil(max + padding)];
  }, [chartData]);

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 rounded-lg bg-[var(--color-bg-secondary)] text-[var(--color-text-muted)]">
        No price data available
      </div>
    );
  }

  return (
    <div className="w-full">
      <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-3">{title}</h3>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%" minHeight={1}>
          <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              domain={priceDomain}
              tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
              tickFormatter={formatPrice}
            />
            <Tooltip content={<PriceTooltip />} />
            <Legend wrapperStyle={{ fontSize: '11px' }} />
            <Area
              type="monotone"
              dataKey="close"
              name="Price"
              stroke="#10b981"
              fill="url(#priceGradient)"
              strokeWidth={2}
            />
            {chartData[0]?.high != null && (
              <Line
                type="monotone"
                dataKey="high"
                name="High"
                stroke="#3b82f6"
                strokeWidth={1}
                strokeDasharray="3 3"
                dot={false}
              />
            )}
            {chartData[0]?.low != null && (
              <Line
                type="monotone"
                dataKey="low"
                name="Low"
                stroke="#ef4444"
                strokeWidth={1}
                strokeDasharray="3 3"
                dot={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function ChatChart({ chart, compact = false }: ChatChartProps) {
  return (
    <div className={`w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-card)] p-3 ${compact ? 'my-2' : 'my-3'}`}>
      {chart.chartType === 'sentiment_history' && (
        <HistoricalChart
          data={chart.data as any[]}
          className={compact ? 'h-56' : 'h-64'}
        />
      )}
      {chart.chartType === 'price_history' && (
        <PriceChart data={chart.data} title={chart.title} />
      )}
      {chart.chartType === 'earnings' && (
        <EarningsChart data={chart.data as any[]} title={chart.title} />
      )}
      {chart.chartType === 'revenue' && (
        <RevenueChart data={chart.data as any[]} title={chart.title} />
      )}
      {chart.chartType === 'cash_flow' && (
        <CashFlowChart data={chart.data as any[]} title={chart.title} />
      )}
      {chart.chartType === 'margins' && (
        <MarginChart data={chart.data as any[]} title={chart.title} />
      )}
      {chart.chartType === 'eps_growth' && (
        <EPSGrowthChart
          data={chart.data as any[]}
          quarterlyData={chart.quarterlyData as any[]}
          title={chart.title}
        />
      )}
      {chart.chartType === 'share_dilution' && (
        <ShareDilutionChart data={chart.data as any[]} title={chart.title} />
      )}
      {chart.chartType === 'shares_outstanding' && (
        <SharesOutstandingChart data={chart.data as any[]} title={chart.title} />
      )}
      {chart.chartType === 'fcf_per_share' && (
        <FCFPerShareChart
          cashFlowData={chart.data as any[]}
          balanceSheetData={(chart.balanceSheetData || []) as any[]}
          title={chart.title}
        />
      )}
    </div>
  );
}
